import React from 'react';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';

export type LabelSize = {
    id: string;
    width: number;
    height: number;
    label: string;
};

export const LABEL_SIZES: LabelSize[] = [
    { id: '35x19', width: 35, height: 19, label: '35 × 19 mm' },
    { id: '40x15', width: 40, height: 15, label: '40 × 15 mm' },
    { id: '40x28', width: 40, height: 28, label: '40 × 28 mm' },
    { id: '40x30', width: 40, height: 30, label: '40 × 30 mm' },
    { id: '50x28', width: 50, height: 28, label: '50 × 28 mm' },
    { id: '51x36', width: 51, height: 36, label: '51 × 36 mm' },
    { id: '60x60', width: 60, height: 60, label: '60 × 60 mm' },
    { id: '69x48', width: 69, height: 48, label: '69 × 48 mm' },
    { id: '70x25', width: 70, height: 25, label: '70 × 25 mm' },
    { id: '75x25', width: 75, height: 25, label: '75 × 25 mm' },
    { id: '100x36', width: 100, height: 36, label: '100 × 36 mm' },
    { id: '100x150', width: 100, height: 150, label: '100 × 150 mm' },
    { id: '102x150', width: 102, height: 150, label: '102 × 150 mm' },
    { id: '25x178', width: 25, height: 178, label: '25 × 178 mm' },
];

export type LabelRenderType = 'qrcode' | 'barcode';
export type LabelDesign = 'standard' | 'compact-left' | 'compact-right' | 'framed' | 'top-code';
export type LabelTheme = 'default' | 'safety-yellow' | 'construction-orange' | 'industrial-gray' | 'midnight-black';

interface LabelGeneratorProps {
    asset: any;
    companyName: string;
    size: LabelSize;
    type: LabelRenderType;
    design?: LabelDesign;
    theme?: LabelTheme;
    hasPadding?: boolean;
}

const getThemeStyles = (theme: LabelTheme, design: LabelDesign) => {
    switch (theme) {
        case 'safety-yellow':
            return {
                bg: 'bg-yellow-400',
                textPrimary: 'text-gray-900',
                textSecondary: 'text-yellow-900/70',
                border: 'border-yellow-500',
                codeBg: 'bg-white'
            };
        case 'construction-orange':
            return {
                bg: 'bg-orange-500',
                textPrimary: 'text-white',
                textSecondary: 'text-orange-100',
                border: 'border-orange-600',
                codeBg: 'bg-white'
            };
        case 'industrial-gray':
            return {
                bg: 'bg-slate-200',
                textPrimary: 'text-slate-900',
                textSecondary: 'text-slate-600',
                border: 'border-slate-300',
                codeBg: 'bg-white'
            };
        case 'midnight-black':
            return {
                bg: 'bg-slate-950',
                textPrimary: 'text-white',
                textSecondary: 'text-gray-400',
                border: 'border-slate-800',
                codeBg: 'bg-white'
            };
        case 'default':
        default:
            if (design === 'framed') {
                return {
                    bg: 'bg-gray-900',
                    textPrimary: 'text-white',
                    textSecondary: 'text-gray-300',
                    border: 'border-gray-900',
                    codeBg: 'bg-white'
                };
            }
            return {
                bg: 'bg-white',
                textPrimary: 'text-gray-900',
                textSecondary: 'text-gray-500',
                border: 'border-gray-200',
                codeBg: 'bg-white'
            };
    }
};

const LabelGenerator: React.FC<LabelGeneratorProps> = ({ asset, companyName, size, type, design = 'standard', theme = 'default', hasPadding = false }) => {
    // Generate the URL or identifier to encode
    const encodeValue = `${window.location.origin}/assets/${asset.id}`;
    // Or if barcode, it's better to use asset tag or ID
    const barcodeValue = asset.id ? asset.id.toString() : 'UNKNOWN';

    const isSmallHeight = size.height <= 20;
    const isMediumHeight = size.height > 20 && size.height <= 36;
    const isLargeHeight = size.height > 36;
    // Special layout for very wide/thin labels
    const isThin = size.width <= 25 && size.height > 100;

    let fontSizeCompany = "text-[6px]";
    let fontSizeAsset = "text-[8px]";
    let qrPadding = "p-0.5";

    if (isMediumHeight) {
        fontSizeCompany = "text-[8px]";
        fontSizeAsset = "text-[10px]";
        qrPadding = "p-1";
    } else if (isLargeHeight) {
        fontSizeCompany = "text-[12px]";
        fontSizeAsset = "text-[14px]";
        qrPadding = "p-2";
    }

    const tStyles = getThemeStyles(theme, design);

    if (isThin) {
        // Vertical layout for 25x178 (Overrides designs because of extreme constraints)
        return (
            <div
                className={`${tStyles.bg} flex flex-col items-center justify-between overflow-hidden border ${tStyles.border}`}
                style={{
                    width: '100%',
                    height: '100%',
                    padding: '4mm 2mm',
                    boxSizing: 'border-box'
                }}
            >
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }} className="flex-1 flex flex-col items-center justify-center font-bold text-center gap-4">
                    <span className={`text-[18px] uppercase tracking-widest ${tStyles.textPrimary}`}>{asset.name || 'Asset Name'}</span>
                    <span className={`text-[12px] opacity-70 ${tStyles.textSecondary}`}>{companyName || 'Company Name'}</span>
                    {asset.tag && (
                        <span className={`text-[10px] items-center gap-1 font-mono mt-2 ${tStyles.textSecondary}`}>
                            {asset.tag}
                        </span>
                    )}
                </div>
                <div className={`mt-4 shrink-0 ${tStyles.codeBg} p-2 rounded-lg shadow-sm border ${tStyles.border} flex items-center justify-center`}>
                    {type === 'qrcode' ? (
                        <div style={{ width: '16mm', height: '16mm' }}>
                            <QRCode
                                value={encodeValue}
                                size={256}
                                style={{ height: "100%", maxWidth: "100%", width: "100%" }}
                                viewBox={`0 0 256 256`}
                            />
                        </div>
                    ) : (
                        <div style={{ transform: 'rotate(90deg)', width: '20px', height: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Barcode
                                value={barcodeValue}
                                height={40}
                                width={1.2}
                                displayValue={false}
                                margin={0}
                                background="transparent"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const renderCode = (isCompact: boolean = false) => {
        if (type === 'qrcode') {
            return (
                <div className={`h-full max-h-full flex items-center justify-center ${tStyles.codeBg} rounded-sm w-full border ${tStyles.border}`}>
                    <div className={`h-full flex items-center justify-center ${qrPadding}`}>
                        <QRCode
                            value={encodeValue}
                            size={256}
                            style={{ height: '100%', maxWidth: '100%', width: 'auto' }}
                            viewBox={`0 0 256 256`}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className={`w-full h-full flex items-center justify-center overflow-hidden ${tStyles.codeBg} rounded-sm border ${tStyles.border}`}>
                    <Barcode
                        value={barcodeValue}
                        height={isCompact ? (size.height > 30 ? 30 : 15) : (size.height > 30 ? 40 : 20)}
                        width={size.width > 60 && !isCompact ? 1.5 : 1}
                        displayValue={!isSmallHeight}
                        fontSize={isSmallHeight ? 0 : isMediumHeight ? 8 : 12}
                        margin={0}
                        background="transparent"
                    />
                </div>
            );
        }
    };

    const containerStyle = {
        width: '100%',
        height: '100%',
        padding: hasPadding ? '5mm' : (isSmallHeight ? '1.5mm' : '2.5mm'),
        boxSizing: 'border-box' as const
    };

    switch (design) {
        case 'compact-left':
            return (
                <div className={`${tStyles.bg} flex items-center overflow-hidden relative border ${tStyles.border}`} style={containerStyle}>
                    <div className="h-full w-[40%] shrink-0 flex items-center justify-center pr-1">
                        {renderCode(true)}
                    </div>
                    <div className="flex-1 h-full flex flex-col justify-center pl-2 border-l border-black/10 min-w-0">
                        <div className={`font-bold leading-tight truncate ${fontSizeCompany} ${tStyles.textSecondary}`}>
                            {companyName || 'Company Name'}
                        </div>
                        <div className={`font-bold leading-tight ${fontSizeAsset} mt-0.5 ${tStyles.textPrimary} break-words overflow-hidden`}>
                            {asset.name || 'Asset Name'}
                        </div>
                        {!isSmallHeight && (
                            <div className={`text-[6px] ${tStyles.textSecondary} mt-1 truncate`}>ID: {asset.tag || asset.id}</div>
                        )}
                    </div>
                </div>
            );

        case 'compact-right':
            return (
                <div className={`${tStyles.bg} flex items-center overflow-hidden relative border ${tStyles.border}`} style={containerStyle}>
                    <div className="flex-1 h-full flex flex-col justify-center pr-2 border-r border-black/10 text-right min-w-0">
                        <div className={`font-bold leading-tight truncate ${fontSizeCompany} ${tStyles.textSecondary}`}>
                            {companyName || 'Company Name'}
                        </div>
                        <div className={`font-bold leading-tight ${fontSizeAsset} mt-0.5 ${tStyles.textPrimary} break-words overflow-hidden`}>
                            {asset.name || 'Asset Name'}
                        </div>
                        {!isSmallHeight && (
                            <div className={`text-[6px] ${tStyles.textSecondary} mt-1 truncate`}>ID: {asset.tag || asset.id}</div>
                        )}
                    </div>
                    <div className="h-full w-[40%] shrink-0 flex items-center justify-center pl-1">
                        {renderCode(true)}
                    </div>
                </div>
            );

        case 'framed':
            return (
                <div className={`${tStyles.bg} flex flex-col justify-between overflow-hidden relative border-[2px] ${tStyles.border}`} style={containerStyle}>
                    <div className={`font-bold text-center leading-tight truncate ${tStyles.textSecondary} ${fontSizeCompany}`}>
                        {companyName || 'Company Name'}
                    </div>

                    <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden my-0.5 pb-0.5">
                        {renderCode()}
                    </div>

                    <div className={`font-bold text-center leading-tight truncate ${tStyles.textPrimary} ${fontSizeAsset}`}>
                        {asset.name || 'Asset Name'}
                    </div>
                </div>
            );

        case 'top-code':
            return (
                <div className={`${tStyles.bg} flex flex-col overflow-hidden relative border ${tStyles.border}`} style={containerStyle}>
                    <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden mb-1 border-b border-black/10 pb-1">
                        {renderCode()}
                    </div>
                    <div className={`font-bold text-center leading-tight truncate ${tStyles.textPrimary} ${fontSizeAsset}`}>
                        {asset.name || 'Asset Name'}
                    </div>
                    {!isSmallHeight && (
                        <div className={`text-center leading-tight truncate ${fontSizeCompany} ${tStyles.textSecondary} mt-0.5`}>
                            {companyName || 'Company Name'}
                        </div>
                    )}
                </div>
            );

        case 'standard':
        default:
            return (
                <div className={`${tStyles.bg} flex flex-col justify-between overflow-hidden relative border ${tStyles.border}`} style={containerStyle}>
                    <div className={`font-bold text-center leading-tight truncate ${fontSizeCompany} ${tStyles.textSecondary}`}>
                        {companyName || 'Company Name'}
                    </div>

                    <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden py-0.5">
                        {renderCode()}
                    </div>

                    <div className={`font-bold text-center leading-tight truncate ${fontSizeAsset} mt-0.5 ${tStyles.textPrimary}`}>
                        {asset.name || 'Asset Name'}
                    </div>
                </div>
            );
    }
};

export default LabelGenerator;
